import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { RevisionReasonDialog } from '@ldt/features/orders/components/revision_reason_dialog';
import { SaveOrderWithReportDialog } from '@ldt/features/orders/components/save_order_with_report_dialog';
import {
  TempusMissingClinicalVariablesDialog,
  type TempusMissingClinicalVariablesSaveOptions,
} from '@ldt/features/orders/components/tempus_missing_clinical_variables_dialog';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Caution } from 'src/components/icons/caution';
import { Trash } from 'src/components/icons/trash';
import {
  OrderStatus,
  UploadedFileCategory,
  LdtUploadedFileFieldsFragment,
} from 'src/types';

/** Props for the LdtFormActions component. */
export interface LdtFormActionsProps {
  showSubmitButton: boolean;
  showSaveButton: boolean;
  showDraftActionButtons: boolean;
  /**
   * When true disables action buttons and shows SUBMITTING... / SAVING... labels.
   */
  isSubmitting?: boolean;
  /**
   * Called when the SUBMIT button is clicked. Should run form validation and
   * return true if the form is valid (confirmation dialog will then open).
   */
  onValidateBeforeSubmit: () => Promise<boolean>;
  /**
   * Called when the SAVE button is clicked. Should run blocking validation and
   * return true before any save confirmation dialog opens.
   */
  onValidateBeforeSave?: () => Promise<boolean>;
  /** Shown under SAVE when onValidateBeforeSave returns false. */
  saveValidationErrorMessage?: string;
  onConfirmSubmit: () => Promise<void>;
  onSaveAsDraft: () => Promise<void>;
  onDiscard: () => Promise<void>;
  requireDiscardConfirmation: boolean;
  onDiscardSuccess?: () => void;
  // At most one of showRevisionReasonDialog / showSaveWithReportDialog applies
  // per save click (Tempus missing-clinical dialog is separate).
  /** Show the RevisionReasonDialog on SAVE (non-Tempus, no AI report). */
  showRevisionReasonDialog: boolean;
  /** Show the SaveOrderWithReportDialog on SAVE when the order has an AI report. */
  showSaveWithReportDialog: boolean;
  /**
   * Tempus CS save on QC Pending. When clinical vars are complete and there is
   * no AI report: save immediately with no dialog (backend → Image Pending).
   * When incomplete: show optional Tempus email checkbox (unchecked save keeps
   * status unchanged). Orders with an AI report use SaveOrderWithReportDialog
   * instead (clinical vars are complete in that case).
   */
  tempusCsSaveConfig?: {
    hasMissingClinicalVariables: boolean;
  };
  /** Called after the user confirms in the RevisionReason dialog. */
  onConfirmSave: (
    qcRevisionReason?: string,
    options?: TempusMissingClinicalVariablesSaveOptions,
  ) => Promise<void>;
  /** Called after the user confirms in the SaveOrderWithReport dialog. */
  onConfirmSaveWithReport: (revertStatus: OrderStatus) => Promise<void>;

  // ── RevisionReasonDialog pass-through props ───────────────────────────────
  onFileSelected?: (file: File, category: UploadedFileCategory) => void;
  onFileRemoved?: (fileId: string) => void;
  isFileDeleting?: (fileId: string) => boolean;
  otherFiles?: LdtUploadedFileFieldsFragment[];
  existingQcRevisionReason?: string | null;
}

/**
 * LDT order form action buttons and their associated confirmation dialogs.
 */
export const LdtFormActions = ({
  showSubmitButton,
  showSaveButton,
  showDraftActionButtons,
  isSubmitting = false,
  onValidateBeforeSubmit,
  onValidateBeforeSave,
  saveValidationErrorMessage = 'Please correct the invalid fields before saving.',
  onConfirmSubmit,
  onSaveAsDraft,
  onDiscard,
  requireDiscardConfirmation,
  onDiscardSuccess,
  showRevisionReasonDialog,
  showSaveWithReportDialog,
  tempusCsSaveConfig,
  onConfirmSave,
  onConfirmSaveWithReport,
  onFileSelected,
  onFileRemoved,
  isFileDeleting,
  otherFiles = [],
  existingQcRevisionReason,
}: LdtFormActionsProps) => {
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveWithReportDialogOpen, setSaveWithReportDialogOpen] =
    useState(false);
  const [tempusMissingClinicalDialogOpen, setTempusMissingClinicalDialogOpen] =
    useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [saveValidationError, setSaveValidationError] = useState<string | null>(
    null,
  );

  const handleSubmitClick = async () => {
    const isValid = await onValidateBeforeSubmit();
    if (isValid) setSubmitDialogOpen(true);
  };

  const handleSaveClick = async () => {
    if (onValidateBeforeSave) {
      const isValid = await onValidateBeforeSave();
      if (!isValid) {
        setSaveValidationError(saveValidationErrorMessage);
        return;
      }
    }

    setSaveValidationError(null);

    if (showSaveWithReportDialog) {
      setSaveWithReportDialogOpen(true);
      return;
    }

    const skipDialogsForCompleteTempusSave =
      tempusCsSaveConfig && !tempusCsSaveConfig.hasMissingClinicalVariables;

    if (skipDialogsForCompleteTempusSave) {
      await onConfirmSave();
      return;
    }

    if (
      tempusCsSaveConfig?.hasMissingClinicalVariables &&
      !showRevisionReasonDialog
    ) {
      setTempusMissingClinicalDialogOpen(true);
      return;
    }

    if (showRevisionReasonDialog) {
      setSaveDialogOpen(true);
      return;
    }

    await onConfirmSave();
  };

  const handleDiscardClick = async () => {
    if (!requireDiscardConfirmation) {
      await onDiscard();
    } else {
      setDiscardDialogOpen(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setSubmitDialogOpen(false);
    await onConfirmSubmit();
  };

  const handleConfirmSave = async (
    qcRevisionReason?: string,
    options?: TempusMissingClinicalVariablesSaveOptions,
  ) => {
    setSaveDialogOpen(false);
    await onConfirmSave(qcRevisionReason, options);
  };

  const handleConfirmSaveWithReport = async (revertStatus: OrderStatus) => {
    setSaveWithReportDialogOpen(false);
    await onConfirmSaveWithReport(revertStatus);
  };

  const handleConfirmTempusMissingClinicalSave = async (
    options: TempusMissingClinicalVariablesSaveOptions,
  ) => {
    setTempusMissingClinicalDialogOpen(false);
    await onConfirmSave(undefined, options);
  };

  const handleConfirmDiscard = async () => {
    setDiscardDialogOpen(false);
    await onDiscard();
    onDiscardSuccess?.();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'stretch', sm: 'space-between' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0.25, sm: 2 },
            order: { xs: 2, sm: 1 },
          }}
        >
          {showDraftActionButtons && (
            <>
              <Button
                type='button'
                variant='text'
                color='primary'
                onClick={handleDiscardClick}
                disabled={isSubmitting}
                size='large'
                sx={{
                  minHeight: { xs: '44px', sm: 'auto' },
                }}
              >
                DISCARD
              </Button>
              <Button
                type='button'
                variant='text'
                color='primary'
                onClick={() => onSaveAsDraft()}
                disabled={isSubmitting}
                size='large'
                sx={{
                  minHeight: { xs: '44px', sm: 'auto' },
                }}
              >
                SAVE AS DRAFT
              </Button>
            </>
          )}
        </Box>
        {showSubmitButton && (
          <Button
            variant='contained'
            color='primary'
            size='large'
            data-testid='order-form-submit-button'
            disabled={isSubmitting}
            sx={{
              width: { xs: '100%', sm: '160px' },
              minHeight: { xs: '48px', sm: 'auto' },
              order: { xs: 1, sm: 2 },
            }}
            onClick={handleSubmitClick}
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
          </Button>
        )}
        {showSaveButton && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'stretch', sm: 'flex-end' },
              gap: 0.5,
              order: { xs: 1, sm: 2 },
            }}
          >
            <Button
              variant='contained'
              color='primary'
              size='large'
              disabled={isSubmitting}
              sx={{
                width: { xs: '100%', sm: '160px' },
                minHeight: { xs: '48px', sm: 'auto' },
              }}
              onClick={handleSaveClick}
              data-testid='order-form-save-button'
            >
              {isSubmitting ? 'SAVING...' : 'SAVE'}
            </Button>
            {saveValidationError && (
              <Typography
                variant='caption'
                color='error'
                data-testid='order-form-save-validation-error'
                sx={{ textAlign: { xs: 'left', sm: 'right' } }}
              >
                {saveValidationError}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Confirm Submission?',
          subtitle:
            'I have reviewed and confirmed the information on the order is correct.',
          type: FeedbackType.Primary,
          icon: Caution,
        }}
        isDialogOpen={submitDialogOpen}
        onPrimaryButtonClick={handleConfirmSubmit}
        onSecondaryButtonClick={() => setSubmitDialogOpen(false)}
        handleDialogClose={() => setSubmitDialogOpen(false)}
        dialogTestId='order-form-confirm-submit-dialog'
        primaryButtonTestId='order-form-confirm-submit-button'
        secondaryButtonTestId='order-form-confirm-submit-cancel-button'
      />
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, DISCARD',
          secondaryButtonText: 'NO',
          title: 'Discard Draft?',
          subtitle:
            'Are you sure you want to discard this draft? This action cannot be undone.',
          type: FeedbackType.Error,
          icon: Trash,
        }}
        isDialogOpen={discardDialogOpen}
        onPrimaryButtonClick={handleConfirmDiscard}
        onSecondaryButtonClick={() => setDiscardDialogOpen(false)}
        handleDialogClose={() => setDiscardDialogOpen(false)}
      />
      {showRevisionReasonDialog && (
        <RevisionReasonDialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          onSubmit={handleConfirmSave}
          onFileSelected={onFileSelected}
          onFileRemoved={onFileRemoved}
          otherFiles={otherFiles}
          isFileDeleting={isFileDeleting}
          existingQcRevisionReason={existingQcRevisionReason}
        />
      )}
      {showSaveWithReportDialog && (
        <SaveOrderWithReportDialog
          open={saveWithReportDialogOpen}
          onClose={() => setSaveWithReportDialogOpen(false)}
          onSubmit={handleConfirmSaveWithReport}
        />
      )}
      {tempusCsSaveConfig && (
        <TempusMissingClinicalVariablesDialog
          open={tempusMissingClinicalDialogOpen}
          onClose={() => setTempusMissingClinicalDialogOpen(false)}
          onSubmit={handleConfirmTempusMissingClinicalSave}
        />
      )}
    </>
  );
};
